/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Typography,
    TextField,
    IconButton,
    Tooltip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Autocomplete,
    Alert,
    Stack,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Backdrop,
    LinearProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EditIcon from "@mui/icons-material/Edit";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import RefreshIcon from "@mui/icons-material/Refresh";
import apiHeaders from "./apiHeaders";
import { handleSessionExpiredClose } from "./logout";
import ImportProgress from "./components/ImportProgress";
import { extractErrorMessage } from "./errorUtils";

const ROW_STATUS = {
    SAVED: "saved",
    MODIFIED: "modified",
    IMPORTED: "imported",
    ERROR: "error",
};

function TaxCodeMapping() {
    const [items, setItems] = useState([]);
    const [taxCodes, setTaxCodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "", source: "" });
    const [isOpen, setIsOpen] = useState(false);
    const [severity, setSeverity] = useState("success");
    const [alertMessage, setAlertMessage] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [error, setError] = useState("");
    const realmId = sessionStorage.getItem("realmId");
    const authHeaders = { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}` };
    const [status, setStatus] = useState("idle");
    const [data, setData] = useState(null);

    const [rowStatuses, setRowStatuses] = useState({});
    const [orphanErrors, setOrphanErrors] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [errorDetail, setErrorDetail] = useState(null);

    // Server-side pagination state - see customerentity_mapping.jsx for the full rationale.
    // `items` holds one page's worth of rows while `serverPaged` is true (the common "just
    // browsing" case, backed by the DB's pageNumber/pageSize query); filtering/searching needs
    // every row, so it switches to "full list" mode (serverPaged=false) on demand.
    const [totalCount, setTotalCount] = useState(0);
    const [serverPaged, setServerPaged] = useState(true);

    // True only while a page-turn/search/mode-switch fetch is in flight - unlike `loading`
    // (which unmounts the whole view), this drives a small inline indicator so the search box
    // and table stay mounted and interactive while the debounced search request is running.
    const [tableLoading, setTableLoading] = useState(false);

    // Holds the pending debounce timer for search-as-you-type, so a search request only ever
    // fires once typing pauses instead of on every keystroke.
    const searchDebounceRef = useRef(null);

    // Edits survive page/mode changes independently of `items`, keyed by itemCode (matching the
    // existing rowStatuses convention below). This is exactly what Save submits - only rows that
    // actually changed - which is safe because the backend's SaveAllAsync only upserts the rows
    // it's given; it never deletes rows absent from the payload.
    const [editedRows, setEditedRows] = useState({});

    const showNotification = (type, msg) => {
        setSeverity(type);
        setAlertMessage(msg);
        setIsOpen(true);
    };

    const handleClose = () => setIsOpen(false);

    // Flags any row whose Zamp tax code no longer exists in the current Zamp tax code list.
    // Applied per-page (see call sites below) rather than once for the whole dataset, since a
    // page fetch now only ever returns the rows for that page.
    const computeDeletedStatuses = (pageItems, taxCodesList) => {
        const validCodes = new Set((taxCodesList || []).map(tc => tc.code));
        const deletedStatuses = {};
        (pageItems || []).forEach(item => {
            if (item.zampTaxCode && !validCodes.has(item.zampTaxCode)) {
                deletedStatuses[item.itemCode] = {
                    status: ROW_STATUS.ERROR,
                    tooltip: `Tax code "${item.zampTaxCode}" no longer exists in Zamp`,
                    error: `Tax code "${item.zampTaxCode}" has been deleted from Zamp`,
                    rowData: { itemCode: item.itemCode, itemName: item.itemName, taxCode: item.zampTaxCode },
                };
            }
        });
        return deletedStatuses;
    };

    // Fetches a single page of product tax-code mappings, optionally narrowed by a search term
    // (applied at the SQL level server-side - see TaxCodeMappingRepository). Used for the default
    // "just browsing" view, including while the user is actively searching.
    const fetchMappingPage = async (pageNumber, pageSizeArg, refresh, searchTermArg = "") => {
        const searchParam = searchTermArg ? `&searchTerm=${encodeURIComponent(searchTermArg)}` : "";
        const res = await fetch(
            `${process.env.REACT_APP_CONNECTOR_API_URL}/api/taxcode/mapping?realmId=${realmId}&refresh=${refresh}&pageNumber=${pageNumber}&pageSize=${pageSizeArg}${searchParam}`,
            { headers: authHeaders }
        );
        if (res.status === 401) {
            handleSessionExpiredClose();
            return null;
        }
        if (!res.ok) {
            throw new Error(await extractErrorMessage(res, "Failed to fetch data"));
        }
        return await res.json(); // { items, totalCount, pageNumber, pageSize }
    };

    // Fetches one page (with the given search term) and applies the result, using the
    // lightweight `tableLoading` indicator rather than the full-page spinner - shared by page
    // changes, rows-per-page changes, debounced search, and returning from full-list mode.
    const fetchServerPage = async (pageNumber, pageSizeArg, searchTermArg) => {
        setTableLoading(true);
        try {
            const pageResult = await fetchMappingPage(pageNumber, pageSizeArg, false, searchTermArg);
            const pageItems = Array.isArray(pageResult?.items) ? pageResult.items : [];
            setItems(pageItems);
            setTotalCount(typeof pageResult?.totalCount === "number" ? pageResult.totalCount : 0);
            setServerPaged(true);
            setRowStatuses(prev => ({ ...prev, ...computeDeletedStatuses(pageItems, taxCodes) }));
        } catch (err) {
            setError(err.message || "Error fetching data");
        } finally {
            setTableLoading(false);
        }
    };

    // Fetches the complete mapping list (no page params) - needed whenever filtering must match
    // against every row, not just whichever page happens to be loaded.
    const fetchAllMappings = async (refresh) => {
        const res = await fetch(
            `${process.env.REACT_APP_CONNECTOR_API_URL}/api/taxcode/mapping?realmId=${realmId}&refresh=${refresh}`,
            { headers: authHeaders }
        );
        if (res.status === 401) {
            handleSessionExpiredClose();
            return [];
        }
        if (!res.ok) {
            throw new Error(await extractErrorMessage(res, "Failed to fetch data"));
        }
        return await res.json(); // plain array, since no page params were sent
    };

    const fetchData = async (refresh = false) => {
        if (!realmId) {
            setError("Missing realm information. Please log in again.");
            setItems([]);
            setTaxCodes([]);
            return;
        }
        setLoading(true);
        setError("");
        try {
            const [pageRes, taxCodesRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/taxcode/mapping?realmId=${realmId}&refresh=${refresh}&pageNumber=1&pageSize=${rowsPerPage}`, {
                    headers: authHeaders,
                }),
                fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/taxcode/zamp?realmId=${realmId}&refresh=${refresh}`, {
                    headers: authHeaders,
                }),
            ]);

            if (pageRes.status === 401 || taxCodesRes.status === 401) {
                handleSessionExpiredClose();
                setItems([]);
                setTaxCodes([]);
                return;
            }

            if (!pageRes.ok || !taxCodesRes.ok) {
                const failedRes = !pageRes.ok ? pageRes : taxCodesRes;
                throw new Error(await extractErrorMessage(failedRes, "Failed to fetch data"));
            }

            const [pageResult, taxCodesData] = await Promise.all([pageRes.json(), taxCodesRes.json()]);
            const pageItems = Array.isArray(pageResult?.items) ? pageResult.items : [];

            setItems(pageItems);
            setTotalCount(typeof pageResult?.totalCount === "number" ? pageResult.totalCount : 0);
            setServerPaged(true);
            setPage(0);
            setEditedRows({});
            setTaxCodes(taxCodesData || []);
            setRowStatuses(computeDeletedStatuses(pageItems, taxCodesData));
            setError("");
        } catch (err) {
            setError(err.message || "Error fetching data");
            setItems([]);
            setTaxCodes([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // Defaults to the cached DB copy (refresh=false), matching CustomerEntityMapping's page load
    // behavior; only the explicit Refresh button click (handleRefresh) passes refresh=true.
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [realmId]);

    // Switches the grid from a single server page into "full list" mode: needed only for the
    // Modified/Imported/Error status chips, since those are local unsaved-edit states with no DB
    // column to filter on - the server can't page/filter by them, so matching against them
    // requires every row to be loaded. Free-text search never needs to call this itself (see
    // handleFilterTextChange below) - it stays server-paginated only while nothing has already
    // pulled the full list into memory (a status filter, or an Import).
    const switchToFullListMode = async () => {
        setTableLoading(true);
        try {
            const fullList = await fetchAllMappings(false);
            setItems(fullList || []);
            setServerPaged(false);
            setPage(0);
            setRowStatuses(prev => ({ ...prev, ...computeDeletedStatuses(fullList, taxCodes) }));
        } catch (err) {
            setError(err.message || "Error fetching data");
        } finally {
            setTableLoading(false);
        }
    };

    // Clears any pending debounced search fetch when the component unmounts.
    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        };
    }, []);

    const handleTaxCodeChange = (itemId, newCode) => {
        setMessage({ type: "", text: "", source: "" });
        setItems(prev => prev.map(it => (it.itemId === itemId ? { ...it, zampTaxCode: newCode } : it)));
        const item = items.find(it => it.itemId === itemId);
        if (item) {
            setRowStatuses(prev => ({
                ...prev,
                [item.itemCode]: { status: ROW_STATUS.MODIFIED, tooltip: "Modified — not yet saved" },
            }));
            setEditedRows(prev => {
                const base = prev[item.itemCode] || item;
                return { ...prev, [item.itemCode]: { ...base, zampTaxCode: newCode } };
            });
        }
        setUnsavedChanges(true);
    };

    const handleSave = async () => {
        if (!realmId) {
            setError("Missing realm information. Please log in again.");
            return;
        }
        const dirtyRows = Object.values(editedRows);
        if (dirtyRows.length === 0) {
            setUnsavedChanges(false);
            return;
        }
        setSaving(true);
        setOrphanErrors([]);
        setStatusFilter("all");
        setMessage({ type: "", text: "", source: "" });
        try {
            // Only the rows that actually changed are submitted - never the whole dataset - so
            // Save stays correct and cheap no matter how many pages were browsed.
            const response = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/taxcode/mapping?realmId=${realmId}`, {
                method: "POST",
                headers: { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}`, "Content-Type": "application/json" },
                body: JSON.stringify(dirtyRows),
            });
            if (response.status === 401) {
                handleSessionExpiredClose();
                return;
            }
            if (!response.ok) {
                throw new Error(await extractErrorMessage(response, "Failed to save mappings"));
            }

            setMessage({ type: "success", text: "Tax code mappings saved successfully!", source: "save" });
            setUnsavedChanges(false);
            setRowStatuses({});
            setEditedRows({});
        } catch (err) {
            setMessage({ type: "error", text: err.message || "Error saving mappings", source: "save" });
        } finally {
            setSaving(false);
        }
    };

    const handleRefresh = () => {
        setMessage({ type: "", text: "", source: "" });
        setRefreshing(true);
        setFilterText("");
        setStatusFilter("all");
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        fetchData(true);
        setData(null);
        setStatus("idle");
        setOrphanErrors([]);
        setUnsavedChanges(false);
    };

    const handleChangePage = async (event, newPage) => {
        setPage(newPage);
        if (serverPaged) {
            await fetchServerPage(newPage + 1, rowsPerPage, filterText.trim());
        }
    };

    const handleChangeRowsPerPage = async (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        if (serverPaged) {
            await fetchServerPage(1, newRowsPerPage, filterText.trim());
        }
    };

    // Free-text search stays server-paginated: it's debounced so a fetch only fires once typing
    // pauses. It's skipped entirely whenever we're not server-paged - that covers both ways the
    // grid ends up holding the complete list in memory (a status filter switch via
    // switchToFullListMode, or a just-completed Import): filteredItems already re-applies
    // filterText against whatever's already loaded, so a server round trip here would only serve
    // to silently discard that in-memory view (and any unsaved edits/import results with it) in
    // favor of a freshly-fetched, unfiltered-by-those-edits server page.
    const handleFilterTextChange = (value) => {
        setFilterText(value);
        setPage(0);
        if (message.text) setMessage({ type: "", text: "", source: "" });

        if (!serverPaged) return;

        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            fetchServerPage(1, rowsPerPage, value.trim());
        }, 300);
    };

    const handleStatusFilterChange = (newStatus) => {
        setStatusFilter(newStatus);
        setPage(0);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        if (newStatus === "all") {
            if (unsavedChanges) {
                // The full list (with pending Import/edit results) is already loaded in memory -
                // clearing the status filter just needs filteredItems to show every row again.
                // Fetching from the server here would silently replace that view with stale DB
                // data until Save or Refresh runs, hiding unsaved work.
                return;
            }
            // Nothing unsaved: safe to resume efficient server-paged browsing, honoring whatever
            // search text is currently in the box.
            fetchServerPage(1, rowsPerPage, filterText.trim());
        } else {
            switchToFullListMode();
        }
    };

    const hasActiveStatuses = Object.keys(rowStatuses).length > 0;
    const deletedTaxCodeCount = Object.values(rowStatuses).filter(s => s.status === ROW_STATUS.ERROR && s.error?.includes("has been deleted")).length;
    const totalRowCount = serverPaged ? totalCount : items.length;

    // Overlays any pending (unsaved) edits onto whatever page/list is currently loaded, so a
    // previously edited row still shows the user's edit even after paging away and back.
    const displayItems = items.map(it => (editedRows[it.itemCode] ? { ...it, ...editedRows[it.itemCode] } : it));

    const filteredItems = (() => {
        // While serverPaged, the text search already ran server-side (see
        // handleFilterTextChange) - re-applying it here against `filterText` would be racy during
        // the debounce window (the input updates immediately; `items` only refreshes once the
        // debounced fetch resolves), so skip it and trust whatever the server returned.
        const search = serverPaged ? "" : filterText.toLowerCase();
        const base = displayItems.filter(it => {
            const matchesText = (it.itemCode?.toLowerCase() ?? "").includes(search) ||
                (it.itemName || "").toLowerCase().includes(search) ||
                (it.zampTaxCode?.toLowerCase() ?? "").includes(search);
            if (!matchesText) return false;
            if (statusFilter === "all") return true;
            const s = rowStatuses[it.itemCode]?.status || ROW_STATUS.SAVED;
            return s === statusFilter;
        });
        if (statusFilter === ROW_STATUS.ERROR && orphanErrors.length > 0) {
            const filtered = orphanErrors.filter(o =>
                o.itemCode.toLowerCase().includes(search) ||
                (o.itemName || "").toLowerCase().includes(search) ||
                (o.zampTaxCode || "").toLowerCase().includes(search)
            );
            return [...base, ...filtered];
        }
        return base;
    })();

    // When serverPaged, `filteredItems` already IS exactly one page's worth of rows fetched from
    // the DB, so it's rendered as-is; otherwise (full list loaded) it's sliced client-side.
    const rowsToRender = serverPaged
        ? filteredItems
        : filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const statusCounts = (() => {
        if (!hasActiveStatuses) return {};
        // NOTE: while serverPaged, this only reflects the currently loaded page - Save itself is
        // unaffected, since it always submits every entry in `editedRows` regardless of page.
        const counts = { [ROW_STATUS.SAVED]: 0, [ROW_STATUS.MODIFIED]: 0, [ROW_STATUS.IMPORTED]: 0, [ROW_STATUS.ERROR]: 0 };
        items.forEach(it => {
            const s = rowStatuses[it.itemCode]?.status || ROW_STATUS.SAVED;
            counts[s]++;
        });
        return counts;
    })();

    const getRowStatusIcon = (item) => {
        const info = rowStatuses[item.itemCode];
        if (!info) {
            return (
                <Tooltip title="Saved" placement="left">
                    <CloudDoneOutlinedIcon sx={{ fontSize: 16, color: "grey.400" }} />
                </Tooltip>
            );
        }
        if (info.status === ROW_STATUS.MODIFIED) {
            return (
                <Tooltip title={info.tooltip} placement="left">
                    <EditIcon sx={{ fontSize: 16, color: "warning.main" }} />
                </Tooltip>
            );
        }
        if (info.status === ROW_STATUS.IMPORTED) {
            return (
                <Tooltip title={info.tooltip} placement="left">
                    <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
                </Tooltip>
            );
        }
        if (info.status === ROW_STATUS.ERROR) {
            return (
                <Tooltip title={info.tooltip} placement="left">
                    <ErrorOutlineIcon
                        sx={{ fontSize: 16, color: "error.main", cursor: "pointer" }}
                        onClick={() => setErrorDetail(info)}
                    />
                </Tooltip>
            );
        }
        return null;
    };

    if (loading) return (
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "calc(100vh - 64px)" }}>
            <CircularProgress size={48} thickness={4} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Loading mappings...
            </Typography>
        </Box>
    );

    const handleExport = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/taxcode/export?realmId=${realmId}`, {
                method: 'GET',
            });

            if (!response.ok) {
                // Surfaces the backend's own message when available (e.g. "Zamp API token is not
                // configured...") instead of a generic failure notice, so the user knows what to fix.
                throw new Error(await extractErrorMessage(response, 'Export failed'));
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            const disposition = response.headers.get('Content-Disposition');
            const formattedDate = new Date().toISOString().split('T')[0];
            let fileName = `product_taxcode_data_${formattedDate}.xlsx`;
            if (disposition && disposition.includes('filename=')) {
                const match = disposition.match(/filename="?([^"]+)"?/);
                if (match && match[1]) {
                    fileName = match[1];
                }
            }
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);
        } catch (err) {
            showNotification('error', err.message || 'Unexpected error occurred');
        }
    };

    const ExportButton = () => (
        <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
        >
            Export
        </Button>
    );

    const handleImport = async (event) => {
        setMessage({ type: "", text: "", source: "" });
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        setStatus("loading");
        setRowStatuses({});
        setOrphanErrors([]);
        setStatusFilter("all");
        setEditedRows({});
        try {
            const response = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/taxcode/import?realmId=${realmId}`, {
                method: 'POST',
                body: formData,
            });

            if (response.status === 400) {
                // A 400 here is either a validation failure or - most commonly - the backend's
                // UserActionRequiredException for a missing Zamp API token (see
                // ExceptionHandlingMiddleware), both returned as JSON `{ message }`. Parsing it
                // instead of showing the raw response body avoids leaking a stringified JSON blob
                // to the user.
                showNotification('error', await extractErrorMessage(response, 'Invalid file format'));
                return;
            }

            if (!response.ok) {
                showNotification('error', await extractErrorMessage(response, 'Import failed'));
                return;
            }

            const json = await response.json();
            setData(json);

            const newStatuses = {};
            const newOrphanErrors = [];
            const importedMappings = json.taxCodeMappings || [];

            // The backend tells us exactly which rows it matched and updated, by itemCode (see
            // ImportProgress.UpdatedRecordIds on the backend). This is the only reliable signal:
            // diffing the import result against `items` (as this used to do) breaks for a large
            // import, because `items` is usually just whichever single page (25-100 rows) was
            // loaded before Import ran, not the full dataset - so a 60k-row import would find
            // almost no "changed" rows, editedRows would end up empty, and Save would silently
            // have nothing to submit.
            const updatedIds = new Set(json.updatedRecordIds || []);
            importedMappings.forEach(newItem => {
                if (updatedIds.has(newItem.itemCode)) {
                    newStatuses[newItem.itemCode] = {
                        status: ROW_STATUS.IMPORTED,
                        tooltip: "Updated via import — not yet saved",
                    };
                }
            });

            if (json.invalidProducts?.length > 0) {
                json.invalidProducts.forEach((prod, i) => {
                    newOrphanErrors.push({
                        itemId: `orphan-prod-${i}`,
                        itemCode: prod,
                        itemName: "",
                        zampTaxCode: "",
                        _orphan: true,
                        _error: "Item not found in the system",
                    });
                });
            }

            if (json.invalidTaxCodes?.length > 0) {
                json.invalidTaxCodes.forEach((tc, i) => {
                    newOrphanErrors.push({
                        itemId: `orphan-tc-${i}`,
                        itemCode: "",
                        itemName: "",
                        zampTaxCode: tc,
                        _orphan: true,
                        _error: `Invalid Zamp Tax Code: "${tc}"`,
                    });
                });
            }

            setRowStatuses(newStatuses);
            setOrphanErrors(newOrphanErrors);

            if (json.summary?.updatedCount) {
                // Import only stages the change in memory (it isn't persisted server-side until
                // Save runs), so the diff buffer must be seeded with exactly the rows Import
                // changed - the same set flagged "Imported" above.
                const importedEdits = {};
                importedMappings.forEach(newItem => {
                    if (newStatuses[newItem.itemCode]) {
                        importedEdits[newItem.itemCode] = newItem;
                    }
                });
                setItems(importedMappings);
                setServerPaged(false); // we already have the complete dataset in hand
                setTotalCount(importedMappings.length);
                setPage(0);
                setEditedRows(importedEdits);
                setUnsavedChanges(true);
            }
            setStatus("completed");
        } catch (err) {
            setData(null);
            setStatus("idle");
            showNotification('error', 'Unexpected error occurred');
        } finally {
            event.target.value = null;
        }
    };

    const handleDismiss = () => {
        setData(null);
        setStatus("idle");
        setOrphanErrors([]);
        setRowStatuses(prev => {
            const next = {};
            Object.entries(prev).forEach(([k, v]) => {
                if (v.status !== ROW_STATUS.ERROR) next[k] = v;
            });
            return next;
        });
        setStatusFilter("all");
    };

    const ImportButton = () => (
        <>
            <input
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                id="import-file"
                onChange={handleImport}
            />
            <label htmlFor="import-file">
                <Button
                    variant="contained"
                    color="primary"
                    component="span"
                    startIcon={<UploadIcon />}
                >
                    Import
                </Button>
            </label>
        </>
    );

    return (
        <Box display="flex" flexDirection="column" gap={2} p={2}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}>
                Product Tax Code Mapping Utility
            </Typography>

            <Paper elevation={6} sx={{ boxShadow: 4, borderRadius: 2, p: 4, maxWidth: 1050, width: "100%", mx: "auto", mt: 1 }}>
                <Stack spacing={2} direction="column" sx={{ width: "100%", mt: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
                        <TextField
                            size="small"
                            label="Filter by name, code, or tax code"
                            placeholder="Start typing to filter by name, code, or tax code"
                            value={filterText}
                            onChange={e => handleFilterTextChange(e.target.value)}
                            sx={{ flexBasis: "50%" }}
                        />
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                            <ImportButton />
                            <ExportButton />
                        </Box>
                        <Tooltip title="Refresh Qbo products and services and Zamp product tax codes">
                            <IconButton
                                type="button"
                                className="zamp-icon-action"
                                onClick={handleRefresh}
                                disabled={refreshing}
                            >
                                <RefreshIcon sx={{ fontSize: 24, animation: refreshing ? "spin 1s linear infinite" : "none" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {status === "loading" && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2">Loading import progress...</Typography>
                        </Box>
                    )}

                    {status === "completed" && (
                        <ImportProgress data={data} onDismiss={handleDismiss} />
                    )}

                    {hasActiveStatuses && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Status:</Typography>
                            <Chip label={`All (${totalRowCount})`} size="small" variant={statusFilter === "all" ? "filled" : "outlined"} onClick={() => handleStatusFilterChange("all")} />
                            {(statusCounts[ROW_STATUS.IMPORTED] || 0) > 0 && (
                                <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label={statusCounts[ROW_STATUS.IMPORTED]} size="small"
                                    variant={statusFilter === ROW_STATUS.IMPORTED ? "filled" : "outlined"} color="success"
                                    onClick={() => handleStatusFilterChange(ROW_STATUS.IMPORTED)} />
                            )}
                            {(statusCounts[ROW_STATUS.MODIFIED] || 0) > 0 && (
                                <Chip icon={<EditIcon sx={{ fontSize: 14 }} />} label={statusCounts[ROW_STATUS.MODIFIED]} size="small"
                                    variant={statusFilter === ROW_STATUS.MODIFIED ? "filled" : "outlined"} color="warning"
                                    onClick={() => handleStatusFilterChange(ROW_STATUS.MODIFIED)} />
                            )}
                            {((statusCounts[ROW_STATUS.ERROR] || 0) > 0 || orphanErrors.length > 0) && (
                                <Chip icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />} label={(statusCounts[ROW_STATUS.ERROR] || 0) + orphanErrors.length} size="small"
                                    variant={statusFilter === ROW_STATUS.ERROR ? "filled" : "outlined"} color="error"
                                    onClick={() => handleStatusFilterChange(ROW_STATUS.ERROR)} />
                            )}
                        </Box>
                    )}

                    {deletedTaxCodeCount > 0 && (
                        <Alert severity="error">
                            {deletedTaxCodeCount} item{deletedTaxCodeCount > 1 ? "s have" : " has a"} tax code{deletedTaxCodeCount > 1 ? "s" : ""} that no longer exist{deletedTaxCodeCount === 1 ? "s" : ""} in Zamp. Please re-map {deletedTaxCodeCount > 1 ? "them" : "it"}.
                        </Alert>
                    )}

                    {error && (
                        <Alert severity="error" onClose={() => setError("")}>{error}</Alert>
                    )}

                    {tableLoading && <LinearProgress />}
                    <TableContainer sx={{ border: "1px solid rgba(224, 224, 224, 1)", borderRadius: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ height: 50, backgroundColor: "grey.400" }}>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "50%", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Item Name</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "50%" }}>Zamp Product Tax Code</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rowsToRender.map((item, index) => {
                                    const isOrphan = !!item._orphan;
                                    const info = isOrphan ? null : rowStatuses[item.itemCode];
                                    const s = isOrphan ? ROW_STATUS.ERROR : info?.status;
                                    const rowBg = s === ROW_STATUS.IMPORTED
                                        ? (index % 2 === 0 ? "#f6faf3" : "#eef5ea")
                                        : s === ROW_STATUS.ERROR
                                            ? (index % 2 === 0 ? "#fef7f7" : "#fdf0f0")
                                            : s === ROW_STATUS.MODIFIED
                                                ? (index % 2 === 0 ? "#fffbf0" : "#fff7e6")
                                                : (index % 2 === 0 ? "white" : "grey.100");

                                    return (
                                        <TableRow key={item.itemId} sx={{ backgroundColor: rowBg }}>
                                            <TableCell sx={{
                                                borderRight: "1px solid rgba(224, 224, 224, 1)",
                                                maxWidth: 300,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}>
                                                <Tooltip
                                                    title={item.itemCode}
                                                    placement="bottom"
                                                    slotProps={{
                                                        popper: { modifiers: [{ name: "offset", options: { offset: [0, 8] } }] },
                                                        tooltip: { sx: { color: "white", fontSize: "0.8rem", borderRadius: 1, px: 1.5, py: 0.5 } }
                                                    }}
                                                >
                                                    <Box component="span" sx={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {item.itemCode}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        {isOrphan ? (
                                                            <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13, fontStyle: "italic", pl: 1 }}>
                                                                {item.zampTaxCode || "-"}
                                                            </Typography>
                                                        ) : (() => {
                                                            const isDeletedCode = item.zampTaxCode && !taxCodes.find(tc => tc.code === item.zampTaxCode);
                                                            const currentValue = taxCodes.find(tc => tc.code === item.zampTaxCode)
                                                                || (isDeletedCode ? { code: item.zampTaxCode } : null);
                                                            return (
                                                                <Autocomplete
                                                                    options={taxCodes}
                                                                    getOptionLabel={(option) => option?.code ?? ""}
                                                                    renderOption={(props, option) => <li {...props}>{option.code}</li>}
                                                                    value={currentValue}
                                                                    onChange={(e, newValue) => handleTaxCodeChange(item.itemId, newValue ? newValue.code : "")}
                                                                    isOptionEqualToValue={(option, value) => option.code === value?.code}
                                                                    renderInput={(params) => (
                                                                        <TextField {...params} size="small"
                                                                            placeholder="Start typing to select tax code"
                                                                            error={!!isDeletedCode}
                                                                            helperText={isDeletedCode ? `"${item.zampTaxCode}" no longer exists` : undefined}
                                                                        />
                                                                    )}
                                                                    fullWidth
                                                                />
                                                            );
                                                        })()}
                                                    </Box>
                                                    <Box sx={{ flexShrink: 0, width: 20, display: "flex", justifyContent: "center" }}>
                                                        {isOrphan ? (
                                                            <Tooltip title={item._error} placement="left">
                                                                <ErrorOutlineIcon
                                                                    sx={{ fontSize: 14, color: "error.main", cursor: "pointer" }}
                                                                    onClick={() => setErrorDetail({
                                                                        error: item._error,
                                                                        rowData: { itemCode: item.itemCode, itemName: item.itemName, taxCode: item.zampTaxCode },
                                                                    })}
                                                                />
                                                            </Tooltip>
                                                        ) : getRowStatusIcon(item)}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={serverPaged ? totalCount : filteredItems.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                        // Disables Next/Previous (and the rows-per-page select) while a page
                        // fetch is in flight, so repeatedly clicking Next doesn't queue up a
                        // burst of requests that then "fast-forward" through pages once they
                        // all resolve.
                        disabled={tableLoading}
                    />

                    <Stack spacing={1} sx={{ mt: 2, width: "100%", maxWidth: 800 }}>
                        {message.source === "save" && message.type === "error" && (
                            <Typography color="error" sx={{ fontSize: 14 }}>{message.text}</Typography>
                        )}
                        {message.source === "save" && message.type === "success" && (
                            <Typography color="success.main" sx={{ fontSize: 14 }}>{message.text}</Typography>
                        )}
                        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !unsavedChanges} sx={{ width: 250 }}
                            startIcon={saving ? <CircularProgress size={18} thickness={5} /> : null}>
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            <Dialog open={!!errorDetail} onClose={() => setErrorDetail(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: "bold", color: "error.main" }}>Error Details</DialogTitle>
                <DialogContent dividers>
                    {errorDetail?.rowData && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Item Code</Typography>
                                <Typography variant="body1" sx={{ fontFamily: "monospace" }}>{errorDetail.rowData.itemCode}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Item Name</Typography>
                                <Typography variant="body1">{errorDetail.rowData.itemName || "-"}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Zamp Tax Code</Typography>
                                <Typography variant="body1" sx={{ fontFamily: "monospace" }}>{errorDetail.rowData.taxCode || "-"}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Error</Typography>
                                <Typography variant="body1" color="error.main">{errorDetail.error}</Typography>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setErrorDetail(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Backdrop open={saving} className="zamp-backdrop-loader" sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
                <CircularProgress size={48} thickness={4} />
                <Typography variant="body1">Saving mappings...</Typography>
            </Backdrop>

            <Snackbar open={isOpen} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default TaxCodeMapping;
